import { Select } from '@/components/ui/Select'
import { SEND_SLOT_OPTIONS, promotionCityOptions, type PromotionComposeFormState } from '@/lib/venue/promotionCampaignViewModel'
import { REGION_STATES } from '@/lib/venue/regionCodes'

function FieldError({ message }: { message: string | undefined }) {
  if (!message) return null
  return <p className="mt-1 text-xs text-danger-500">{message}</p>
}

/**
 * 지역 홍보 작성 — 발송 지역(시/도→시/군구 캐스케이딩) + 시간대 선택(요구사항 "발송 대상 조건(발송
 * 지역 선택, 시간대, 광고 수신 동의 사용자 — 미동의·철회 자동 제외 안내)").
 *
 * 🚨 "이어서 작성"(기존 DRAFT 편집)에서는 `readOnly`를 켠다 — 대상 조건은 작성 시점에 동결되며
 * 이후 어떤 EP로도 바꿀 수 없다(#714 §3).
 */
export function PromotionTargetPicker({
  form,
  errors,
  readOnly = false,
  onChangeRegionState,
  onChangeRegionCity,
  onChangeSendSlot,
}: {
  form: PromotionComposeFormState
  errors: { region?: string }
  readOnly?: boolean
  onChangeRegionState: (regionStateId: string) => void
  onChangeRegionCity: (regionCityId: string | null) => void
  onChangeSendSlot: (sendSlot: PromotionComposeFormState['sendSlot']) => void
}) {
  const cities = promotionCityOptions(form.regionStateId)

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label className="mb-1.5 block text-sm font-medium text-ink-700" htmlFor="promotion-region-state">
            시/도
          </label>
          <Select
            id="promotion-region-state"
            value={form.regionStateId ?? ''}
            disabled={readOnly}
            aria-invalid={Boolean(errors.region)}
            onChange={(event) => {
              onChangeRegionState(event.target.value)
            }}
          >
            <option value="" disabled>
              시/도 선택
            </option>
            {REGION_STATES.map((state) => (
              <option key={state.stateId} value={state.stateId}>
                {state.stateName}
              </option>
            ))}
          </Select>
          <FieldError message={errors.region} />
        </div>

        <div>
          <label className="mb-1.5 block text-sm font-medium text-ink-700" htmlFor="promotion-region-city">
            시/군/구
          </label>
          <Select
            id="promotion-region-city"
            value={form.regionCityId ?? ''}
            disabled={readOnly || !form.regionStateId}
            onChange={(event) => {
              onChangeRegionCity(event.target.value || null)
            }}
          >
            <option value="">시/도 전체</option>
            {cities.map((city) => (
              <option key={city.cityId} value={city.cityId}>
                {city.cityName}
              </option>
            ))}
          </Select>
        </div>
      </div>

      <div>
        <label className="mb-1.5 block text-sm font-medium text-ink-700" htmlFor="promotion-send-slot">
          시간대
        </label>
        <Select
          id="promotion-send-slot"
          value={form.sendSlot ?? ''}
          disabled={readOnly}
          onChange={(event) => {
            onChangeSendSlot((event.target.value || null) as PromotionComposeFormState['sendSlot'])
          }}
        >
          <option value="">전체 시간대</option>
          {SEND_SLOT_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Select>
      </div>

      <p className="text-xs text-slateish-600">
        광고 수신에 동의한 사용자에게만 발송됩니다. 미동의·동의 철회 회원은 자동으로 제외됩니다.
      </p>
    </div>
  )
}
